import axios from 'axios';
import {
  ID,
  ListApiResponse,
  SingleApiResponse,
  SuccessApiResponse
} from '@ianesthesie-kit/services';
import {
  BillingEntity,
  BillingEntityMember,
  HostedLink,
  OnboardingLinkPayload,
  RegisterReaderPayload,
  TerminalReader
} from './billingEntity.model';

/**
 * BFF client for the Stripe billing entities of the current hospital (`/billing/entity`).
 * The hospital and the calling doctor travel via headers — never sent from here. Static-only,
 * following {@link BillingLineService}'s surface.
 */
export class BillingEntityService {
  private static BASE_URL = '/billing/entity';

  public static fetchEntities(): Promise<BillingEntity[]> {
    return axios
      .get<ListApiResponse<BillingEntity>>(this.BASE_URL)
      .then((resp) => resp.data.data);
  }

  public static refreshEntity(entityId: ID): Promise<BillingEntity> {
    return axios
      .post<SingleApiResponse<BillingEntity>>(
        `${this.BASE_URL}/${entityId}/refresh`
      )
      .then((resp) => resp.data.data);
  }

  /**
   * Single-use hosted onboarding link, valid for a few minutes. The first call for an entity
   * also creates its Stripe connected account, so it can take a few seconds.
   */
  public static createOnboardingLink(
    entityId: ID,
    payload: OnboardingLinkPayload
  ): Promise<HostedLink> {
    return axios
      .post<SingleApiResponse<HostedLink>>(
        `${this.BASE_URL}/${entityId}/onboarding-link`,
        payload
      )
      .then((resp) => resp.data.data);
  }

  /** Single-use hosted Stripe dashboard link; members of the entity only. */
  public static createLoginLink(entityId: ID): Promise<HostedLink> {
    return axios
      .post<SingleApiResponse<HostedLink>>(
        `${this.BASE_URL}/${entityId}/login-link`
      )
      .then((resp) => resp.data.data);
  }

  /** Every assignment of the hospital, across all its entities. Admins only. */
  public static fetchMembers(): Promise<BillingEntityMember[]> {
    return axios
      .get<ListApiResponse<BillingEntityMember>>(`${this.BASE_URL}/member`)
      .then((resp) => resp.data.data);
  }

  /** Upsert: a doctor assigned to another entity is moved to this one. */
  public static assignDoctor(
    entityId: ID,
    doctorId: ID
  ): Promise<SuccessApiResponse> {
    return axios
      .put<SuccessApiResponse>(
        `${this.BASE_URL}/${entityId}/member/${doctorId}`
      )
      .then((resp) => resp.data);
  }

  public static unassignDoctor(doctorId: ID): Promise<SuccessApiResponse> {
    return axios
      .delete<SuccessApiResponse>(`${this.BASE_URL}/member/${doctorId}`)
      .then((resp) => resp.data);
  }

  /** Every terminal reader of the hospital, across all its entities. */
  public static fetchReaders(): Promise<TerminalReader[]> {
    return axios
      .get<ListApiResponse<TerminalReader>>(`${this.BASE_URL}/reader`)
      .then((resp) => resp.data.data);
  }

  /** Pairs a physical reader with the entity's Stripe account. Admins only. */
  public static registerReader(
    entityId: ID,
    payload: RegisterReaderPayload
  ): Promise<TerminalReader> {
    return axios
      .post<SingleApiResponse<TerminalReader>>(
        `${this.BASE_URL}/${entityId}/reader`,
        payload
      )
      .then((resp) => resp.data.data);
  }

  public static deleteReader(readerId: ID): Promise<SuccessApiResponse> {
    return axios
      .delete<SuccessApiResponse>(`${this.BASE_URL}/reader/${readerId}`)
      .then((resp) => resp.data);
  }
}
